import React, { useState } from "react";
import { Avatar, AvatarFallback } from "components/ui/avatar";
import { Moon, Sun, Globe, LogOut, CircleDollarSign, FileText } from "lucide-react";
import { useThemeStore } from "context/useThemeStore";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "components/ui/dialog";
import { Progress } from "components/ui/progress_v2";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "components/ui/dropdown-menu_v2";
import { Badge } from "components/ui/badge_v6";
import localization from "services/localization";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";

import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";

const UserMenu = ({ language, handleLanguageChange, userCredit, appUser, user, handleLogout }) => {
  const { theme, setTheme } = useThemeStore();
  const [lang, setLang] = useState(language);
  const [openPrivacy, setOpenPrivacy] = useState(false);
  // const defaultLayoutPluginInstance = defaultLayoutPlugin();

  const getInitials = (user) => {
    const value = user?.nickname || user?.name;

    if (!value) return "U";
    const cleanValue = value.trim();
    if (cleanValue.includes(".")) {
      const parts = cleanValue.split(".").filter(Boolean);

      if (parts.length >= 2) {
        return ((parts[0][0] || "") + (parts[1][0] || "")).toUpperCase();
      }

      return parts[0][0].toUpperCase();
    }
    if (cleanValue.includes(" ")) {
      const parts = cleanValue.split(" ").filter(Boolean);

      if (parts.length >= 2) {
        return ((parts[0][0] || "") + (parts[1][0] || "")).toUpperCase();
      }

      return parts[0][0].toUpperCase();
    }
    return cleanValue[0].toUpperCase();
  };

  const select_bg = theme === "dark" ? "bg-[#121928] text-white" : "bg-white text-foreground";
  const select_bg2 = theme === "dark" ? " text-white" : "text-foreground";

  return (
    <div className="flex items-center gap-4">
      {/* Credits Info */}
      {appUser?.role != "root" && appUser?.role != "student" && window?.location?.pathname !== "/schools" && (
        // <div className="flex flex-col items-end gap-1">
        //   <span className="flex items-center gap-1 text-xs page-subtitle">
        //     <CircleDollarSign className="w-4 h-4" />
        //     {userCredit.creditsAvailable}/{userCredit.totalCredits}
        //   </span>
        //   <Progress
        //     value={userCredit.usedPercentage}
        //     className="h-1 w-28 rounded-full"
        //     style={{
        //       borderRadius: "9999px",
        //       "--progress-color":
        //         userCredit.usedPercentage > 80
        //           ? "#d32f2f" // red
        //           : userCredit.usedPercentage > 50
        //             ? "#f57c00" // orange
        //             : "var(--primary-icon)", // default color
        //     }}
        //   />
        // </div>

        <div className="flex flex-col items-end gap-1">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="cursor-pointer">
                  <span className="flex items-center gap-1 text-xs page-subtitle pb-1">
                    <CircleDollarSign className="w-4 h-4" />
                    {userCredit.creditsUsed}/{userCredit.totalCredits}
                  </span>
                  <div className="relative h-1.5 w-28 overflow-hidden rounded-full bg-muted flex">
                    {/* Used */}
                    <div
                      className="h-full bg-[var(--primary-icon)]"
                      style={{ width: `${userCredit.usedPercentage}%` }}
                    />

                    {/* Frozen */}
                    <div className="h-full bg-blue-400" style={{ width: `${userCredit.frozenPercentage}%` }} />
                  </div>
                </div>
              </TooltipTrigger>

              <TooltipContent className="z-50 bg-[var(--card-bg)]">
                <div className="text-xs space-y-1 w-40">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-muted" />
                      <span className="text-[var(--sidebar-foreground)]">Total Allocated</span>
                    </div>
                    <span className="font-medium text-[var(--sidebar-foreground)]">{userCredit.totalCredits}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-blue-400" />
                      <span className="text-[var(--sidebar-foreground)]">Frozen</span>
                    </div>
                    <span className="font-medium text-[var(--sidebar-foreground)]">{userCredit.creditsFrozen}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-[var(--primary-icon)]" />
                      <span className="text-[var(--sidebar-foreground)]">Used</span>
                    </div>
                    <span className="font-medium text-[var(--sidebar-foreground)]">{userCredit.creditsUsed}</span>
                  </div>

                  <div className="flex items-center justify-between border-t pt-1 mt-1">
                    <span className="text-muted-foreground text-[var(--sidebar-foreground)]">Available</span>
                    <span className="font-semibold text-[var(--sidebar-foreground)]">
                      {userCredit.creditsAvailable}
                    </span>
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}

      {/* Email + Role + Avatar */}
      <div className="flex items-center gap-3">
        {/* Email + Role stacked */}
        <div className="flex flex-col items-start">
          <span className="text-sm font-medium page-title">{user?.email}</span>
          <span className="text-xs page-subtitle">
            {appUser?.role ? appUser.role[0].toUpperCase() + appUser.role.slice(1) : ""}
          </span>
        </div>

        {/* Avatar Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="focus:outline-none">
              <Avatar className="h-10 w-10 cursor-pointer">
                <AvatarFallback className="text-sm font-semibold">{getInitials(user)}</AvatarFallback>
              </Avatar>
              <Badge variant="destructive" className="absolute top-2 right-6 h-4 px-1 !text-[9px] leading-none">
                BETA
              </Badge>
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel>{localization.account || "Account"}</DropdownMenuLabel>
            <DropdownMenuSeparator />

            {/* Dark Mode Toggle */}
            <DropdownMenuItem onClick={() => setTheme(theme === "dark" ? "light" : "dark")}>
              {theme === "dark" ? <Sun className="mr-2 w-4 h-4" /> : <Moon className="mr-2 w-4 h-4" />}
              {theme === "dark" ? "Light Mode" : "Dark Mode"}
            </DropdownMenuItem>

            {/* Language Switch */}
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <Globe className="mr-2 w-4 h-4" />
                {localization.language || "Language"}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuRadioGroup
                  value={lang}
                  onValueChange={(v) => {
                    setLang(v);
                    handleLanguageChange(v === "En" ? "En" : "It");
                  }}
                >
                  <DropdownMenuRadioItem value="En">{localization.english || "English"}</DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="It">{localization.italian || "Italian"}</DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
              </DropdownMenuSubContent>
            </DropdownMenuSub>

            <DropdownMenuItem onClick={() => setOpenPrivacy(true)}>
              <FileText className="mr-2 w-4 h-4" />
              {localization.privacy_policy || "Privacy Policy"}
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            {/* Logout */}
            <DropdownMenuItem className="text-red-500" onClick={handleLogout}>
              <LogOut className="mr-2 w-4 h-4" />
              {localization.logout || "Logout"}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Dialog open={openPrivacy} onOpenChange={setOpenPrivacy} className={select_bg}>
        <DialogContent className={`!max-w-6xl w-full  ${select_bg}`}>
          <div className="max-h-[80vh] overflow-y-auto border rounded-md mt-3 text-muted-foreground">
            {/* <iframe src={"/General.pdf"} title="Privacy Policy" className="w-full h-[600px] rounded-md border" /> */}
            <Worker workerUrl={"/pdf.worker.min.js"}>
              <Viewer fileUrl={"/General.pdf"} />
              {/* plugins={[defaultLayoutPluginInstance]} */}
            </Worker>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserMenu;
